import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const isFilledDot = (type) => {
  // Match the screenshot feel: payments as hollow markers, most other events as filled.
  return type !== 'payment_received';
};

const formatDateLabel = (occurredAt) => {
  const d = dayjs(occurredAt);
  if (!d.isValid()) return String(occurredAt || '');
  return d.format('DD MMM, YYYY');
};

const formatTimeLabel = (occurredAt) => {
  const d = dayjs(occurredAt);
  if (!d.isValid()) return null;
  const t = d.format('HH:mm');
  return t === '00:00' ? null : t;
};

const formatInvoiceItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return items.filter(Boolean).join(', ');
};

const ClientHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await apiService.get(`/clients/${id}/history`);
        if (!mounted) return;

        if (res?.success) {
          setClient(res?.data?.client || null);
          setHistory(Array.isArray(res?.data?.history) ? res.data.history : []);
        } else {
          setError(res?.message || res?.error || 'Failed to fetch client history');
        }
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to fetch client history');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchHistory();
    return () => {
      mounted = false;
    };
  }, [id]);

  const clientName = useMemo(() => {
    if (!client) return '';
    return `${client.first_name || ''} ${client.last_name || ''}`.trim();
  }, [client]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/dashboard/clients')} aria-label="Back to clients">
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Client History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {clientName || (client?.mobile ? `Client #${client?.id} • ${client?.mobile}` : `Client #${id}`)}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              No activity yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This client doesn’t have any recorded activity.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {history.map((item, idx) => {
              const filled = isFilledDot(item?.type);
              const isLast = idx === history.length - 1;
              const dateLabel = formatDateLabel(item?.occurred_at);
              const timeLabel = formatTimeLabel(item?.occurred_at);

              return (
                <Box
                  key={`${item?.type || 'event'}-${item?.occurred_at || idx}-${idx}`}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                >
                  <Box
                    sx={{
                      width: 140,
                      textAlign: 'right',
                      color: 'text.secondary',
                      pt: 0.15,
                      fontSize: 13,
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <div>{dateLabel}</div>
                    {timeLabel ? <div style={{ fontSize: 12 }}>{timeLabel}</div> : null}
                  </Box>

                  <Box sx={{ position: 'relative', width: 22, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        mt: 0.35,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: filled ? 'primary.main' : 'transparent',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        boxSizing: 'border-box',
                      }}
                    />
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          bottom: -18,
                          width: 2,
                          bgcolor: 'divider',
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {item?.title || 'Activity'}
                    </Typography>
                    {item?.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {item.description}
                      </Typography>
                    ) : null}
                    {item?.type === 'invoice_created' ? (
                      <Box sx={{ mt: 0.5 }}>
                        {formatInvoiceItems(item?.meta?.services) ? (
                          <Typography variant="body2" color="text.secondary">
                            Services: {formatInvoiceItems(item.meta.services)}
                          </Typography>
                        ) : null}
                        {formatInvoiceItems(item?.meta?.products) ? (
                          <Typography variant="body2" color="text.secondary">
                            Products: {formatInvoiceItems(item.meta.products)}
                          </Typography>
                        ) : null}
                        {formatInvoiceItems(item?.meta?.memberships) ? (
                          <Typography variant="body2" color="text.secondary">
                            Memberships: {formatInvoiceItems(item.meta.memberships)}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ClientHistory;

